import { apiClient } from './api-client';

export type OfflineFlowSize = 'ok' | 'near-limit' | 'over-limit';

export interface OfflineKitPreflightFlow {
  flowId: string;
  versionId: string;
  name: string;
  version: number;
  estimatedBytes: number;
  size: OfflineFlowSize;
  imageCount: number;
  missingImageCount: number;
  unsupported: Array<{ nodeId: string; type: string; label?: string }>;
}

export interface OfflineKitStatus {
  preflight: {
    flows: OfflineKitPreflightFlow[];
    totalEstimatedBytes: number;
    shellAvailable: boolean;
    limits: { flowMaxBytes: number; flowWarnBytes: number; emailBytes: number };
  };
  lastExport: { at: string; downloadId: string; by: { name: string; email: string } } | null;
  changes: { updated: number; added: number; removed: number } | null;
}

/** The server's filename carries the download ID, so keep it when we can read it. */
function fileNameFrom(disposition: string | undefined): string {
  const match = disposition?.match(/filename="([^"]+)"/);
  return match?.[1] ?? `offline-kit-${new Date().toISOString().slice(0, 10)}.zip`;
}

export const offlineKitApi = {
  async status(): Promise<OfflineKitStatus> {
    const { data } = await apiClient.get('/offline-kit/status');
    return data;
  },

  /** Generate a kit and hand it to the browser as a file download. */
  async download(): Promise<{ fileName: string; skippedImages: number }> {
    const response = await apiClient.get('/offline-kit', {
      responseType: 'blob',
      // Building a large kit reads every image from storage.
      timeout: 5 * 60 * 1000,
    });

    const fileName = fileNameFrom(response.headers['content-disposition']);
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoked on a delay: some browsers start the save asynchronously.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);

    return {
      fileName,
      skippedImages: Number(response.headers['x-offline-kit-skipped-images'] ?? 0) || 0,
    };
  },
};
